<template>
    <div class="dynamic-pricing-rule-component">
        <div :class="loadMask"></div>
        <div>
            <div class="row" v-if="errors.message">
                <div class="col-lg-12">
                    <span class="error">{{ t(errors.message) }}</span>
                </div>
            </div>
            <div class="row">
                <div class="col-lg-12 ">
                    <label>{{ t('Name') }}</label>
                    <input type="text" min="1" maxlength="100" v-model="inputs.name" :placeholder="t('Rule name')"
                           class="form-control">
                    <span v-if="hasError('name')" class="error">{{ errors.name }}</span>
                </div>
            </div>

            <!-- Price parameter section-->
            <div class="row">
                <div class="col-lg-12">
                    <h4>{{ t('Base price') }}</h4>
                </div>
                <div class="col-lg-6">
                    <label>{{ t('Set parameter') }} ({{ t('parameter name which contains purchase price') }})</label>
                    <input type="text" maxlength="50" v-model="inputs.priceAttributeName" class="form-control">
                    <span v-if="hasError('priceAttributeName')" class="error">{{ t(errors.priceAttributeName) }}</span>
                </div>
            </div>

            <!-- Minimal price section -->
            <div class="row">
                <div class="col-lg-12">
                    <h4>{{ t('Set minimal profit') }}</h4>
                </div>
                <div class="col-lg-4">
                    <label>{{ t('Value') }}</label>
                    <input type="number" min="0" :max="maxInputValue" class="controls form-control"
                           v-model="inputs.minimalProfitValue"/>
                    <span v-if="hasError('minimalProfitValue')" class="error">{{ t(errors.minimalProfitValue) }}</span>
                </div>
                <div class="col-lg-4">
                    <label>{{ t('Kind') }}</label>
                    <multiselect
                        :placeholder="t('Select')"
                        :options="getTypeOptions()"
                        :multiple="false"
                        :close-on-select="true"
                        :clear-on-select="false"
                        track-by="id"
                        label="name"
                        v-model="inputs.minimalProfitKind"
                    >
                        <div slot="noOptions">{{ t('List is empty') }}</div>
                    </multiselect>
                </div>
                <span v-if="hasError('minimalProfitKind')" class="error">{{ t(errors.minimalProfitKind) }}</span>
            </div>
            <!-- end of Minimal price section -->

            <!-- Maximal price section -->
            <div class="row">
                <div class="col-lg-12">
                    <h4>{{ t('Set maximal profit') }}</h4>
                </div>
                <div class="col-lg-4">
                    <label>{{ t('Value') }}</label>
                    <input type="number" min="0" :max="maxInputValue" class="controls form-control"
                           v-model="inputs.maximalProfitValue"/>
                    <span v-if="hasError('maximalProfitValue')" class="error">{{ t(errors.maximalProfitValue) }}</span>
                </div>
                <div class="col-lg-4">
                    <label>{{ t('Kind') }}</label>
                    <multiselect
                        :placeholder="t('Select')"
                        :options="getTypeOptions()"
                        :multiple="false"
                        :close-on-select="true"
                        :clear-on-select="false"
                        track-by="id"
                        label="name"
                        v-model="inputs.maximalProfitKind"
                    >
                        <div slot="noOptions">{{ t('List is empty') }}</div>
                    </multiselect>
                </div>
                <span v-if="hasError('maximalProfitKind')" class="error">{{ t(errors.maximalProfitKind) }}</span>
            </div>
            <!-- end of Max price section -->

            <!-- Competition section -->
            <div class="row">
                <div class="col-lg-12">
                    <h4>{{ t('Competition') }}</h4>
                </div>
            </div>
            <div class="row align-items-end">
                <div class="col-lg-4 form-group">
                    <label>{{ t('Set competitions') }} ({{ t('leave empty to get all') }})</label>
                    <multiselect
                        :placeholder="t('Select')"
                        :options="availableShops"
                        :multiple="true"
                        :limit="10"
                        :options-limit="1000"
                        :limit-text="limitText"
                        :close-on-select="false"
                        :clear-on-select="false"
                        track-by="id"
                        label="name"
                        :loading="shopsLoadingFilter"
                        v-model="inputs.shops"
                        @open="onOpenShops"
                    >
                        <div slot="noOptions">{{ t('List is empty') }}</div>
                    </multiselect>
                </div>
                <div class="col-lg-4 form-group" v-if="selectedShopIsAuctions">
                    <label>{{ t('Set sellers') }} ({{ t('leave empty to get all') }})</label>
                    <multiselect
                        :placeholder="t('Select')"
                        :options="availableSellers"
                        :multiple="true"
                        :limit="10"
                        :options-limit="1000"
                        :limit-text="limitText"
                        :close-on-select="false"
                        :clear-on-select="false"
                        group-values="rows"
                        group-label="name"
                        :group-select="true"
                        track-by="id"
                        label="name"
                        :loading="sellersLoadingFilter"
                        v-model="inputs.sellers"
                        @open="onOpenSellers"
                    >
                        <div slot="noOptions">{{ t('List is empty') }}</div>
                    </multiselect>
                </div>
                <div class="col-lg-4 form-group">
                    <label>{{ t('Indicate a competitive benchmark price') }}</label>
                    <multiselect
                        :placeholder="t('Select')"
                        :options="getPriceStrategies()"
                        :multiple="false"
                        :close-on-select="true"
                        :clear-on-select="false"
                        track-by="id"
                        label="name"
                        v-model="inputs.priceStrategy"
                    >
                        <div slot="noOptions">{{ t('List is empty') }}</div>
                    </multiselect>
                </div>
                <span v-if="hasError('priceStrategy')" class="error">{{ t(errors.priceStrategy) }}</span>
            </div>
            <!-- end of Competition section -->

            <!-- Strategy section -->
            <div class="row">
                <div class="col-lg-12">
                    <h4>{{ t('Strategy') }}</h4>
                </div>
                <div class="col-lg-4 form-group">
                    <label>{{ t('Set strategy') }}</label>
                    <multiselect
                        :placeholder="t('Select')"
                        :options="getStrategies()"
                        :multiple="false"
                        :close-on-select="true"
                        :clear-on-select="false"
                        track-by="id"
                        label="name"
                        v-model="inputs.strategy"
                    >
                        <div slot="noOptions">{{ t('List is empty') }}</div>
                    </multiselect>
                </div>
                <div class="col-lg-4 form-group" v-if="displayStrategyValueComputed">
                    <label>{{ t('Value') }}</label>
                    <input type="number" min="0" :max="maxInputValue" class="controls form-control"
                           v-model="inputs.strategyValue"/>
                    <span v-if="hasError('strategyValue')" class="error">{{ t(errors.strategyValue) }}</span>
                </div>
                <div class="col-lg-4 form-group" v-if="displayStrategyPosition">
                    <label>{{ t('Position nr') }}</label>
                    <input type="number" min="1" max="100" class="controls form-control"
                           v-model="inputs.strategyPosition"/>
                </div>
                <div class="col-lg-4 form-group" v-if="displayStrategyValueComputed">
                    <label>{{ t('Kind') }}</label>
                    <multiselect
                        :placeholder="t('Select')"
                        :options="getTypeOptions()"
                        :multiple="false"
                        :close-on-select="true"
                        :clear-on-select="false"
                        track-by="id"
                        label="name"
                        v-model="inputs.strategyKind"
                    >
                        <div slot="noOptions">{{ t('List is empty') }}</div>
                    </multiselect>
                    <span v-if="hasError('strategyKind')" class="error">{{ t(errors.strategyKind) }}</span>
                </div>
                <div class="col-lg-12" v-if="errors.strategy">
                    <span class="error">{{ t(errors.strategy) }}</span>
                </div>
            </div>
            <!-- end of Strategy section -->

            <div class="row">
                <div class="col-lg-12 form-group">
                    <h4>{{ t('Round') }}</h4>
                    <span>{{
                            t('Verification of the minimum price occurs after calculating the price for the product. The subsequent rounding of the price may cause it to be lower than the minimum price.')
                        }}</span>
                </div>
                <div class="col-lg-4">
                    <label>{{ t('Round type') }}</label>
                    <multiselect
                        :placeholder="t('Select')"
                        :options="getRoundType()"
                        :multiple="false"
                        :close-on-select="true"
                        :clear-on-select="false"
                        track-by="id"
                        label="name"
                        v-model="inputs.roundType"
                    >
                        <div slot="noOptions">{{ t('List is empty') }}</div>
                    </multiselect>
                    <span v-if="hasError('roundType')" class="error">{{ t(errors.roundType) }}</span>
                </div>

                <div class="col-lg-4 form-group">
                    <label>{{ t('Round mode') }}</label>
                    <multiselect
                        :placeholder="t('Select')"
                        :options="getRoundDirection()"
                        :multiple="false"
                        :close-on-select="true"
                        :clear-on-select="false"
                        track-by="id"
                        label="name"
                        v-model="inputs.roundMode"
                    >
                        <div slot="noOptions">{{ t('List is empty') }}</div>
                    </multiselect>
                    <span v-if="hasError('roundMode')" class="error">{{ t(errors.roundMode) }}</span>
                </div>

                <div class="col-lg-4">
                    <label>{{ t('Difference from rounded amount') }}</label>
                    <input type="number" min="-10" max="10" class="controls form-control"
                           v-model="inputs.roundDifferent"/>
                    <span v-if="hasError('roundDifferent')" class="error">{{ errors.roundDifferent }}</span>
                </div>

            </div>

            <div class="row">
                <div class="col-lg-12">
                    <button class="btn btn-success btn-confirm"
                            @click.prevent="submit"
                    >{{ t('Submit') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Multiselect from "@/components/Multiselect.vue";
import State from "@/Models/State.ts";
import ValueChecker from "@/services/ValueChecker";

export default {
    components: {
        Multiselect
    },
    props: {
        config: {
            Type: Object
        },
        eventData: {
            Type: Object
        },
        eventBus: {
            type: Object,
        },
    },
    data: function () {
        return {
            maxInputValue: 10000000,
            minInputValue: -10000000,
            availableShops: [],
            availableSellers: [],
            shopsLoadingFilter: false,
            sellersLoadingFilter: false,
            loadMask: '',
            inputs: {
                shops: [],
                sellers: [],
                name: '',
                priceAttributeName: 'PURCHASE_PRICE',
                minimalProfitKind: this.getTypeOptions()[0],
                minimalProfitValue: 0.01,
                maximalProfitKind: null,
                maximalProfitValue: null,
                priceStrategy: null,
                strategy: null,
                strategyValue: 0.01,
                strategyKind: this.getTypeOptions()[0],
                strategyPosition: 1,
                roundType: this.getRoundType()[0],
                roundMode: this.getRoundDirection()[0],
                roundDifferent: 0,
            },
            errors: {},
            events: [],
            limitText(count) {
                return this.$trans('and %count% other items', {count: count});
            },
            stateOrigin: new State()
        };
    },
    beforeDestroy() {
    },
    async mounted() {
        //lets edit
        if (this.eventData && this.eventData.name) {
            this.addLoadMask();
            let data = await this.getRule(this.eventData.name);

            if ('duplicate' === this.eventData.type) {
                data.name += ` (${this.$translate.text('copy')})`;
                delete this.eventData.name;
            }

            await this.setFormData(data);
            this.removeLoadMask();
        }
        this.stateOrigin.value = this.inputs;
    },
    watch: {
        'inputs.shops': {
            handler(actualShops, selectedShops) {
                if (!this.isHandlerNeeded(actualShops, selectedShops)) {
                    return;
                }

                const actualShopsId = actualShops.map(shop => shop.id);
                const selectedShopsId = selectedShops.map(shop => shop.id);

                const removedShopIds = selectedShopsId.filter(id => !actualShopsId.includes(id));
                const sellerIdsToRemove = this.availableSellers
                    .filter(group => removedShopIds.includes(group.id))
                    .flatMap(group => group.rows.map(s => s.id));

                this.inputs.sellers = this.inputs.sellers.filter(s => !sellerIdsToRemove.includes(s.id));
            },
            deep: true
        }
    },
    computed: {
        displayStrategyPosition() {
            return this.inputs.strategy && ['set_on_position'].includes(this.inputs.strategy.id);
        },
        selectedShopIsAuctions() {
            return Array.isArray(this.inputs.shops) && this.inputs.shops.some(shop => shop.auctions);
        },
        displayStrategyValueComputed() {
            return this.inputs.strategy && ['be_on_top'].includes(this.inputs.strategy.id);
        },
    },
    methods: {
        getMode() {
            return this.eventData && this.eventData.type || 'create';
        },
        isHandlerNeeded(actualShops, selectedShops) {
            if (!Array.isArray(selectedShops)) {
                return false;
            }

            if (0 === selectedShops.length)  {
                return false;
            }

            return actualShops.length < selectedShops.length;
        },
        displayStrategyValue() {
            return this.inputs.strategy && ['be_on_top'].includes(this.inputs.strategy.id);
        },
        getPriceStrategies() {
            return [{
                id: 'min_price',
                name: this.$translate.text('Lowest price'),
            }, {
                id: 'avg_price',
                name: this.$translate.text('Average price'),
            }, {
                id: 'highest_price',
                name: this.$translate.text('Highest price')
            }];
        },
        getRoundDirection() {
            return [{
                id: 'UP',
                name: this.$translate.text('Up'),
            }, {
                id: 'DOWN',
                name: this.$translate.text('Down'),
            }];
        },
        getRoundType() {
            return [{
                id: '2',
                name: this.$translate.text('To two decimal places'),
            }, {
                id: '1',
                name: this.$translate.text('To one decimal place'),
            }, {
                id: '0',
                name: this.$translate.text('To full amount'),
            }];
        },
        getStrategies() {
            return [{
                id: 'follow',
                name: this.$translate.text('Follow')
            }, {
                id: 'be_on_top',
                name: this.$translate.text('Beat the price')
            }, {
                id: 'set_on_position',
                name: this.$translate.text('Set on position')
            }];
        },
        getTypeOptions() {
            return [
                {
                    id: 'amount',
                    name: this.$translate.text('amount')
                }, {
                    id: 'percent',
                    name: '%'
                }
            ];
        },
        async getAvailableShops() {
            const url = this.config['shopsFilterUrl'];
            let response = await this.$http['get'](url, {});
            this.availableShops = response.data.data;
        },
        async onOpenShops() {
            if (0 < this.availableShops.length) {
                return;
            }

            this.shopsLoadingFilter = true;
            await this.getAvailableShops();
            this.shopsLoadingFilter = false;
        },
        async onOpenSellers() {
            this.sellersLoadingFilter = true;
            await this.getAvailableSellers();
            this.sellersLoadingFilter = false;
        },
        async getAvailableSellers() {
            const url = this.config['sellersFilterUrl'];
            let shops = this.inputs.shops.map(item => item.id);
            let response = await this.$http['get'](url, {params: {shops: shops}});
            this.availableSellers = response.data.data.map(shopGroup => ({
                ...shopGroup,
                rows: shopGroup.rows.map(seller => ({
                    ...seller,
                    shopId: shopGroup.id
                }))
            }));
        },
        async submit() {
            this.addLoadMask();

            if (!await this.validate()) {
                this.removeLoadMask();

                return;
            }

            let formula = this.buildFormula();
            let formData = this.getFormData(formula);
            let url = this.getSubmitUrl();
            let response;
            let params = {};

            if (this.getMode().includes('edit')) {
                params = {params: {name: this.eventData.name}};
            }

            try {
                response = await this.$http['post'](url, formData, params);
                this.stateOrigin.resetState(this.inputs);
                let eventManagerService = this.eventBus.getEventManager();
                let afterSubmitEvents = this.config.emitEvents['afterSubmit'];

                if (afterSubmitEvents) {
                    eventManagerService.emitEvents(afterSubmitEvents, [{
                        eventData: this.eventData
                    }]);
                }
            } catch (error) {
                this.addError('message', error.data.message);
            } finally {
                this.removeLoadMask();
            }
        },
        async checkRuleExists() {
            const url = this.config['checkRuleExistsUrl'];
            let response = await this.$http['get'](url, {
                params: {
                    name: this.inputs.name,
                    currentName: this.eventData.name
                }
            });

            return response.data.exists;
        },
        async getRule(name) {
            const url = this.config['getRuleUrl'];
            let response = await this.$http['get'](url, {params: {name: name}});

            return response.data;
        },
        async validateName() {
            this.clearError('name');

            if (null === this.inputs.name) {
                this.addRequiredError('name');

                return;
            }

            if (1 > this.inputs.name.trim().length) {
                this.addError('name', this.$trans('This field should contain at least %number% characters', {number: 1}));

                return;
            }

            if (100 < this.inputs.name.trim().length) {
                this.addError('name', this.$trans('This field should contain at most %number% characters', {number: 100}));

                return;
            }

            if (await this.checkRuleExists()) {
                this.addError('name', this.$trans('Rule already exists.'));
            }
        },
        async validate() {
            for (let member in this.errors) delete this.errors[member];
            await this.validateName();

            this.validatePriceAttribute();

            if (null === this.inputs.minimalProfitKind) {
                this.addRequiredError('minimalProfitKind');
            }

            this.validateNumberValue('minimalProfitValue', 'minimalProfitKind', true);

            this.validateNumberValue('maximalProfitValue', 'maximalProfitKind', false);

            if (null === this.inputs.priceStrategy) {
                this.addRequiredError('priceStrategy');
            }

            if (null === this.inputs.strategy) {
                this.addRequiredError('strategy');
            }

            this.validateStrategyValue('strategyValue', 'strategyKind');

            this.validateRound();

            if (null === this.inputs.roundMode) {
                this.addRequiredError('roundDirection');
            }

            return 0 === Object.keys(this.errors).length;
        },
        validateRound() {
            if (ValueChecker.isUndefinedOrNull(this.inputs.roundType)) {
                this.addRequiredError('roundType');

                return;
            }

            if (ValueChecker.isUndefinedOrNull(this.inputs.roundMode)) {
                this.addRequiredError('roundMode');

                return;
            }

            if (ValueChecker.isUndefinedOrNull(this.inputs.roundDifferent)) {
                this.addRequiredError('roundDifferent');

                return;
            }

            if (this.inputs.roundDifferent > 10) {
                this.addError('roundDifferent', this.$trans('This field should be less than %value%', {value: 10}));
            }

            if (this.inputs.roundDifferent < -10) {
                this.addError('roundDifferent', this.$trans('This field should be greater than %value%', {value: -10}));
            }
        },
        validatePriceAttribute() {
            let value = this.inputs.priceAttributeName.trim();

            if (null === value || 0 === value.length) {
                this.addRequiredError('priceAttributeName');

                return;
            }

            if (3 > value.length) {
                this.addError('name', this.$trans('This field should contain at least %number% characters', {number: 3}));

                return;
            }

            if (50 < value.length) {
                this.addError('name', this.$trans('This field should contain at most %number% characters', {number: 100}));
            }
        },
        validateStrategyValue(value, kind) {
            if (!this.displayStrategyValue()) {
                return;
            }

            this.validateNumberValue(value, kind);
        },
        validateNumberValue(value, kind, required) {
            if (ValueChecker.isUndefinedOrNull(this.inputs[value]) || 0 === this.inputs[value].length) {
                if (required) {
                    this.addRequiredError(value);
                }

                return;
            }

            if (!this.inputs[kind]) {
                return;
            }

            if ('percent' === this.inputs[kind].id) {
                if (this.inputs[value] > 100) {
                    this.addError(value, this.$trans('This field should be less than %value%', {value: 100}));

                    return;
                }

                if (this.inputs[value] < -100) {
                    this.addError(value, this.$trans('This field should be greater than %value%', {value: -100}));

                    return;
                }
            }

            if (this.inputs[value] > this.maxInputValue) {
                this.addError(value, this.$trans('This field should be less than %value%', {value: this.maxInputValue}));
            }

            if (this.inputs[value] < this.minInputValue) {
                this.addError(value, this.$trans('This field should be greater than %value%', {value: this.minInputValue}));
            }
        },
        buildFormula() {
            let minimalProfitFormula = this.getMinimalProfitFormula();
            let maximalProfitFormula = this.getMaximalProfitFormula();
            let priceStrategyFormula = this.getPriceStrategyFormula();
            let strategyFormula = this.getStrategyFormula().replaceAll('__PRICE_STRATEGY__', priceStrategyFormula);
            let purchasePriceAttributeName = `$${this.inputs.priceAttributeName}`;

            if (maximalProfitFormula) {
                let maximalFormula = `IF(LESSTHAN(${maximalProfitFormula};__PRICE_STRATEGY__);VALUE(${maximalProfitFormula});__PRICE_STRATEGY__)`
                    .replaceAll('__PRICE_STRATEGY__', strategyFormula);

                strategyFormula = maximalFormula;
            }

            let formula = `ADD({ROUND(IF(NOTEMPTYCOLLECTION($OFFERS);IF(GTETHAN(${minimalProfitFormula};${priceStrategyFormula});VALUE(${minimalProfitFormula});__PRICE_STRATEGY__);VALUE($SRP_PRICE));${this.inputs.roundType.id};${this.inputs.roundMode.id});${this.inputs.roundDifferent}})`
                .replaceAll('__PRICE_STRATEGY__', strategyFormula)
                .replaceAll('__BASE_PRICE__', purchasePriceAttributeName)
                .replaceAll('$OFFERS', '$$$$OFFERS');

            return formula;
        },
        getStrategyFormula() {
            switch (this.inputs.strategy.id) {
                case 'follow':
                    return `SUB({__PRICE_STRATEGY__;${this.getStrategyKindFormula()}})`;
                case 'be_on_top':
                    return `SUB({__PRICE_STRATEGY__;${this.getStrategyKindFormula()}})`;
                case 'set_on_position':
                    return `SETONPOSITION($OFFERS;${this.inputs.strategyPosition})`;
            }
        },
        getPriceStrategyFormula() {
            switch (this.inputs.priceStrategy.id) {
                case 'min_price':
                    return 'MIN($OFFERS)';
                case 'avg_price':
                    return 'AVG($OFFERS)';
                case 'highest_price':
                    return 'MAX($OFFERS)';
            }
        },
        getMinimalProfitFormula() {
            switch (this.inputs.minimalProfitKind.id) {
                case 'amount':
                    return 'ADD({__BASE_PRICE__;__MINIMAL_PROFIT_VALUE__})'.replace('__MINIMAL_PROFIT_VALUE__', this.inputs.minimalProfitValue);
                case 'percent':
                    return 'ADD({__BASE_PRICE__;__MINIMAL_PROFIT_VALUE__})'.replace('__MINIMAL_PROFIT_VALUE__', `MUL({__BASE_PRICE__;PERCENT(${this.inputs.minimalProfitValue})})`);
            }
        },
        getMaximalProfitFormula() {
            if (!this.inputs.maximalProfitKind) {
                return;
            }

            switch (this.inputs.maximalProfitKind.id) {
                case 'amount':
                    return 'ADD({__BASE_PRICE__;__MAXIMAL_PROFIT_VALUE__})'.replace('__MAXIMAL_PROFIT_VALUE__', this.inputs.maximalProfitValue);
                case 'percent':
                    return 'ADD({__BASE_PRICE__;__MAXIMAL_PROFIT_VALUE__})'.replace('__MAXIMAL_PROFIT_VALUE__', `MUL({__BASE_PRICE__;PERCENT(${this.inputs.maximalProfitValue})})`);
            }
        },
        getStrategyKindFormula() {
            switch (this.inputs.strategyKind.id) {
                case 'amount':
                    return `${this.inputs.strategyValue}`;
                case 'percent':
                    return `MUL({__PRICE_STRATEGY__;PERCENT(${this.inputs.strategyValue})})`;
            }
        },
        getFormData(formula) {
            return {
                shops: this.inputs.shops.map(item => item.id),
                sellers: this.inputs.sellers.map(item => item.id),
                name: this.inputs.name,
                formula: formula,
                inputs: JSON.stringify(this.inputs)
            }
        },
        async setFormData(data) {
            let serializedShops = JSON.parse(data.shops);
            let shops = [];
            let sellers = [];

            if (!ValueChecker.isEmptyForAllTypes(serializedShops)) {
                await this.getAvailableShops();
                shops = this.availableShops.filter(shop => serializedShops.includes(shop.id));
            }

            let serializedSellers = JSON.parse(data.sellers);

            if (!ValueChecker.isEmptyForAllTypes(serializedSellers)) {
                await this.getAvailableSellers();

                this.availableSellers.map((seller) => {
                    sellers = sellers.concat(seller.rows.filter(sel => serializedSellers.includes(sel.id)));
                });
            }

            this.inputs = JSON.parse(data.inputs);
            this.inputs.name = data.name;
            this.inputs.shops = shops;
            this.inputs.sellers = sellers;
        },
        addLoadMask() {
            this.loadMask = 'lmask';
        },
        removeLoadMask() {
            this.loadMask = '';
        },
        addRequiredError(key) {
            this.addError(key, this.$trans('This field is required'));
        },
        addError(key, value) {
            this.$set(this.errors, key, value);
        },
        clearError(key) {
            this.$set(this.errors, key, undefined);
            delete this.errors[key];
        },
        hasError(key) {
            return key in this.errors;
        },
        getSubmitUrl() {
            return this.getMode().includes('edit') ? this.config['editUrl'] : this.config['createUrl'];
        },
        async canCloseModal() {
            if (!this.stateOrigin.isEqual(this.inputs)) {
                return await this.hasInputsChange();
            }

            return true;
        },
        hasInputsChange() {
            return new Promise((resolve) => {
                this.$root.showConfirmModalResolve('Confirm?', resolve);
            });
        }
    }
};
</script>

<style scoped>
.dynamic-pricing-rule-component {
    flex: 1;
}

input[type=checkbox], input[type=radio] {
    margin: 4px 0 0;
    margin-top: 1px \9;
    line-height: normal;
    box-shadow: none;
}

input[type=number] {
    box-shadow: none;
    border-radius: 5px;
}

.error {
    color: red;
}

.row.align-items-end {
    display: flex;
    align-items: flex-end;
}

</style>
<style>

.multiselect__placeholder {
    padding-top: 0 !important;
}
</style>



// WEBPACK FOOTER //
// DynamicPricingRuleComponent.vue?144ad1f6